/**
 * Browser polyfills for cross-browser compatibility
 */

// ResizeObserver polyfill for older browsers
if (typeof window !== 'undefined' && !window.ResizeObserver) {
  // Dynamic import to avoid bundling if not needed
  import('resize-observer-polyfill').then(({ default: ResizeObserver }) => {
    window.ResizeObserver = ResizeObserver;
  });
}

// IntersectionObserver polyfill for older browsers
if (typeof window !== 'undefined' && !window.IntersectionObserver) {
  import('intersection-observer').then(() => {
    // Polyfill is automatically applied
  });
}

// File System Access API feature detection and fallback
export const hasFileSystemAccess = () => {
  if (typeof window === 'undefined') return false;

  return (
    'showOpenFilePicker' in window &&
    'showSaveFilePicker' in window &&
    'showDirectoryPicker' in window
  );
};

// Smooth scroll polyfill for Safari < 15.4
if (
  typeof window !== 'undefined' &&
  !('scrollBehavior' in document.documentElement.style)
) {
  import('smoothscroll-polyfill').then(({ default: smoothscroll }) => {
    smoothscroll.polyfill();
  });
}

// RequestIdleCallback polyfill for Safari
if (typeof window !== 'undefined' && !window.requestIdleCallback) {
  window.requestIdleCallback = function (
    callback: IdleRequestCallback,
    options?: IdleRequestOptions
  ) {
    const start = Date.now();
    return setTimeout(function () {
      callback({
        didTimeout: false,
        timeRemaining: function () {
          return Math.max(0, 50 - (Date.now() - start));
        },
      } as IdleDeadline);
    }, options?.timeout || 1) as unknown as number;
  };

  window.cancelIdleCallback = function (id: number) {
    clearTimeout(id);
  };
}

// Web Animations API polyfill for older browsers
if (typeof window !== 'undefined' && !Element.prototype.animate) {
  import('web-animations-js').then(() => {
    // Polyfill is automatically applied
  });
}

// TextEncoder/TextDecoder polyfills for older browsers
if (typeof window !== 'undefined') {
  if (!window.TextEncoder) {
    import('fast-text-encoding').then(() => {
      // Polyfill is automatically applied
    });
  }
}

// Clipboard API polyfill for older browsers
if (typeof window !== 'undefined' && !navigator.clipboard) {
  // Fallback clipboard implementation
  (navigator as any).clipboard = {
    writeText: async (text: string) => {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();

      try {
        document.execCommand('copy');
      } finally {
        document.body.removeChild(textarea);
      }
    },
    readText: async () => {
      // Reading is not supported in fallback
      throw new Error('Clipboard read not supported in this browser');
    },
    write: async () => {
      throw new Error('Clipboard write not supported in this browser');
    },
    read: async () => {
      throw new Error('Clipboard read not supported in this browser');
    },
  } as unknown as Clipboard;
}

// Crypto.randomUUID polyfill for older browsers
if (
  typeof window !== 'undefined' &&
  typeof crypto !== 'undefined' &&
  !crypto.randomUUID
) {
  crypto.randomUUID = function randomUUID(): `${string}-${string}-${string}-${string}-${string}` {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    }) as `${string}-${string}-${string}-${string}-${string}`;
  };
}

// structuredClone polyfill for older browsers (Safari < 15.4)
if (typeof window !== 'undefined' && !window.structuredClone) {
  window.structuredClone = function structuredClone<T>(obj: T): T {
    // Use MessageChannel for deep cloning
    return new Promise((resolve) => {
      const { port1, port2 } = new MessageChannel();
      port2.onmessage = (ev) => resolve(ev.data);
      port1.postMessage(obj);
    }) as unknown as T;
  };
}

// CSS.supports polyfill for older browsers
if (
  typeof window !== 'undefined' &&
  typeof CSS !== 'undefined' &&
  !CSS.supports
) {
  const testElement = document.createElement('div');
  CSS.supports = function (
    propertyOrSupportsCondition: string,
    value?: string
  ): boolean {
    // Simple implementation for property-value pairs
    if (value !== undefined) {
      testElement.style.cssText = '';
      testElement.style.setProperty(propertyOrSupportsCondition, value);
      return (
        testElement.style.getPropertyValue(propertyOrSupportsCondition) !== ''
      );
    }

    // For supports conditions, we can't easily polyfill
    return false;
  };
}

// Export browser detection utilities
export const browserInfo = {
  isChrome:
    typeof window !== 'undefined' &&
    /Chrome/.test(navigator.userAgent) &&
    /Google Inc/.test(navigator.vendor),
  isFirefox:
    typeof window !== 'undefined' && /Firefox/.test(navigator.userAgent),
  isSafari:
    typeof window !== 'undefined' &&
    /Safari/.test(navigator.userAgent) &&
    /Apple Computer/.test(navigator.vendor),
  isEdge: typeof window !== 'undefined' && /Edg/.test(navigator.userAgent),
  isMobile:
    typeof window !== 'undefined' &&
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    ),
  supportsFileSystemAccess: hasFileSystemAccess(),
  supportsWebWorkers: typeof Worker !== 'undefined',
  supportsServiceWorker:
    typeof window !== 'undefined' && 'serviceWorker' in navigator,
  supportsIndexedDB: typeof window !== 'undefined' && 'indexedDB' in window,
  supportsWebGL: (() => {
    if (typeof window === 'undefined') return false;
    try {
      const canvas = document.createElement('canvas');
      return !!(
        window.WebGLRenderingContext &&
        (canvas.getContext('webgl') || canvas.getContext('experimental-webgl'))
      );
    } catch {
      return false;
    }
  })(),
};
